import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ADDRESS_PAIRS, SAME_AS_BILLING_LABEL } from '@/lib/address-pairs';
import { ApiError, api } from '@/lib/api';
import {
  CONTACT_NAME_LABELS,
  applyNameEdit,
  composeFullName,
  splitFullName,
} from '@/lib/contact-name';
import { toDateInputValue } from '@/lib/format';
import { leadSourceInputValue } from '@/lib/lead-source';
import { useUserOptions } from '@/lib/use-user-options';
import { USER_GROUP_SLUGS, narrowingNote, outsideGroupNote } from '@/lib/user-groups';
import { cx } from '@/lib/cx';
import { Button, Combobox, Field, Input, Modal, Textarea } from '@/components/ui';
import type { ComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { ContactTitleInput } from '@/components/contacts/ContactTitleInput';
import type { AddressFields, Company, Contact, ContactInput, UserSummary } from '@/lib/types';

export interface ContactFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Omitted = create mode. Provided = edit mode. */
  contact?: Contact;
  /** Pre-selects and locks the company when opened from a company record. */
  lockedCompany?: { id: number; name: string };
  /**
   * Create mode only: starting values for a person who already arrived
   * somewhere else — an enquiry. They are SUGGESTIONS the person checks before
   * saving, never a write; ignored on an edit.
   */
  prefill?: ContactPrefill;
  onSaved: (contact: Contact) => void;
}

/** What an enquiry knows about the person, as the contact form's starting values. */
export interface ContactPrefill {
  name: string;
  /** Only when the source stored the parts separately; otherwise split from `name`. */
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  title?: string | null;
}

/* -------------------------------------------------------------------------
 * Form shape
 *
 * Every writable field is held as a string (ids included) so that the edit
 * baseline and the live form can be compared key-by-key with `!==`. That
 * comparison is the whole PATCH story: an omitted key is left untouched by the
 * API and an explicit null clears the column, so only genuinely changed keys
 * may be sent. `emailOptOut` is the one exception — it is coerced rather than
 * nullish server-side, so it stays a real boolean.
 * --------------------------------------------------------------------- */

type StringKey =
  | 'name'
  | 'firstName'
  | 'lastName'
  | 'layout'
  | 'companyId'
  | 'accountLeadId'
  | 'clientSuccessManagerId'
  | 'title'
  | 'contactRole'
  | 'department'
  | 'email'
  | 'secondaryEmail'
  | 'countryCode'
  | 'mobile'
  | 'phone'
  | 'website'
  | 'linkedin'
  | 'dateOfBirth'
  | 'leadSource'
  | keyof AddressFields
  | 'description';

type FormState = Record<StringKey, string> & { emailOptOut: boolean };

/** Nullable text columns: identical key names in the form and the payload. */
const TEXT_KEYS = [
  /*
   * First name is required by `validate`, so only Last name can reach the
   * "" → null mapping on a patch. `buildCreate` sends both name parts
   * explicitly (owner, 2026-10-16), not through this list's omit-when-blank.
   */
  'firstName',
  'lastName',
  'title',
  'contactRole',
  'department',
  'email',
  'secondaryEmail',
  'countryCode',
  'mobile',
  'phone',
  'website',
  'linkedin',
  'leadSource',
  'billingStreet',
  'billingCity',
  'billingState',
  'billingCode',
  'billingCountry',
  'shippingStreet',
  'shippingCity',
  'shippingState',
  'shippingCode',
  'shippingCountry',
  'description',
] as const satisfies readonly (keyof ContactInput & StringKey)[];

/*
 * ⚠️ **`ownerId` was a THIRD member here until 2026-08-31 and its absence is now
 * the guard.** The owner retired the field on contacts along with companies,
 * deals, quotes and invoices; measured live that day, **0 of 169 contacts carry
 * an owner**, so nothing was hidden.
 *
 * It left `StringKey`, `ContactInput` and this list together, which is what
 * makes the removal structural: **taking the CONTROL out is not taking the
 * WRITE out.** Left here with no picker behind it, `ownerId` would sit at `''`
 * while the record held an owner, `changedKeys` would notice the difference the
 * first time anything else on the form was edited, and the patch would carry
 * `ownerId: null` — which CLEARS the column, with a 200 and nothing to restore
 * from.
 */
const ID_KEYS = ['accountLeadId', 'clientSuccessManagerId'] as const satisfies readonly (
  keyof ContactInput & StringKey
)[];

/**
 * The two staff pickers.
 *
 * Both are gated by a user group and the server 422s a non-member, so each
 * takes its own narrowed option list — keyed records rather than one shared
 * list, so a picker cannot quietly inherit the wrong options.
 */
type StaffFieldKey = (typeof ID_KEYS)[number];

/*
 * ⚠️ `ADDRESS_PAIRS` is imported, not declared. It used to be a local copy of
 * the same ten column names that `CompanyFormModal` also carried, and the two
 * had already drifted in the way the pairing exists to prevent — this form
 * compares its halves TRIMMED and that one compares them raw. Only the LIST is
 * shared; the comparison below is still this form's own, deliberately.
 *
 * `satisfies` is unnecessary now: every use indexes `form`, so a key that is
 * not a `StringKey` is a compile error at the call site rather than here.
 */

/** Mirrors the server's max lengths exactly — no stricter, no looser. */
const LIMITS: Partial<Record<StringKey, number>> = {
  name: 240,
  firstName: 120,
  lastName: 120,
  layout: 60,
  title: 120,
  contactRole: 120,
  department: 120,
  leadSource: 120,
  email: 255,
  secondaryEmail: 255,
  countryCode: 8,
  mobile: 32,
  phone: 32,
  website: 255,
  linkedin: 255,
  billingStreet: 255,
  billingCity: 120,
  billingState: 120,
  billingCode: 32,
  billingCountry: 120,
  shippingStreet: 255,
  shippingCity: 120,
  shippingState: 120,
  shippingCode: 32,
  shippingCountry: 120,
  description: 20000,
};

const LABELS: Record<StringKey | 'emailOptOut', string> = {
  name: CONTACT_NAME_LABELS.name,
  firstName: CONTACT_NAME_LABELS.firstName,
  lastName: CONTACT_NAME_LABELS.lastName,
  layout: 'Layout',
  companyId: 'Account',
  accountLeadId: 'Account lead',
  clientSuccessManagerId: 'Client success manager',
  title: 'Title',
  contactRole: 'Contact role',
  department: 'Department',
  email: 'Email',
  secondaryEmail: 'Secondary email',
  countryCode: 'Country code',
  mobile: 'Mobile',
  phone: 'Phone',
  website: 'Website',
  linkedin: 'LinkedIn',
  dateOfBirth: 'Date of birth',
  leadSource: 'Lead source',
  billingStreet: 'Street',
  billingCity: 'City',
  billingState: 'State',
  billingCode: 'Postal code',
  billingCountry: 'Country',
  shippingStreet: 'Street',
  shippingCity: 'City',
  shippingState: 'State',
  shippingCode: 'Postal code',
  shippingCountry: 'Country',
  description: 'Notes',
  emailOptOut: 'Email opt out',
};

type SectionId = 'role' | 'details' | 'personal' | 'address' | 'more' | 'notes';

/**
 * Which disclosure holds each field. A server error inside a closed section
 * would otherwise be invisible, so a failed save opens the sections it lands in.
 */
const FIELD_SECTION: Partial<Record<string, SectionId>> = {
  contactRole: 'role',
  department: 'role',
  accountLeadId: 'role',
  clientSuccessManagerId: 'role',
  leadSource: 'role',
  secondaryEmail: 'details',
  countryCode: 'details',
  phone: 'details',
  website: 'details',
  linkedin: 'details',
  dateOfBirth: 'personal',
  emailOptOut: 'personal',
  billingStreet: 'address',
  billingCity: 'address',
  billingState: 'address',
  billingCode: 'address',
  billingCountry: 'address',
  shippingStreet: 'address',
  shippingCity: 'address',
  shippingState: 'address',
  shippingCode: 'address',
  shippingCountry: 'address',
  layout: 'more',
  description: 'notes',
};

const KNOWN_FIELDS = new Set<string>([...Object.keys(LABELS), 'emailOptOut']);

/* Deliberately loose: the server checks the format, and a stricter pattern here
 * would reject addresses the API accepts. Phone, website and linkedin get no
 * format check at all — they are length-only server-side. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/*
 * ⚠️ *SUPERSEDED 2026-10-16:* a local `splitName` split Full name into the parts
 * as Full name was typed. The direction is now the other way round (owner:
 * "Full name, First name and Last name … full consistency"): the person types
 * First and Last, and Full name follows them until it is edited by hand. The one
 * rule lives in `@/lib/contact-name`; `splitFullName` there is used only to
 * PREFILL the parts from a single-string source (an enquiry, a legacy row).
 *
 * ⚠️ *SUPERSEDED 2026-10-16 (later the same day; owner: "if one is filled other
 * 2 get auto filled and vice versa"):* the one-way `fullNameTouched` flag is
 * gone. All three inputs go through `applyNameEdit` — First/Last fill Full name,
 * Full name fills First/Last — whenever the three agreed before the keystroke;
 * a deliberate mismatch ("Dr. A. R. Menon") is left alone. See `setField`.
 */

function blankForm(): FormState {
  return {
    name: '',
    firstName: '',
    lastName: '',
    layout: '',
    companyId: '',
    accountLeadId: '',
    clientSuccessManagerId: '',
    title: '',
    contactRole: '',
    department: '',
    email: '',
    secondaryEmail: '',
    countryCode: '',
    mobile: '',
    phone: '',
    website: '',
    linkedin: '',
    dateOfBirth: '',
    leadSource: '',
    billingStreet: '',
    billingCity: '',
    billingState: '',
    billingCode: '',
    billingCountry: '',
    shippingStreet: '',
    shippingCity: '',
    shippingState: '',
    shippingCode: '',
    shippingCountry: '',
    description: '',
    emailOptOut: false,
  };
}

/** The saved record as form values — the baseline every PATCH diff is taken against. */
function recordForm(contact: Contact | undefined): FormState {
  if (!contact) return blankForm();
  return {
    name: contact.name,
    firstName: contact.firstName ?? '',
    lastName: contact.lastName ?? '',
    layout: contact.layout,
    companyId: contact.company ? String(contact.company.id) : '',
    /*
     * ⚠️ No `ownerId` baseline, and its absence is deliberate rather than an
     * oversight. Seeding the stored owner would put it back into `values` and
     * from there into `changedKeys` — which is precisely the clearing write
     * that removing the picker exists to prevent.
     */
    accountLeadId: contact.accountLead ? String(contact.accountLead.id) : '',
    clientSuccessManagerId: contact.clientSuccessManager
      ? String(contact.clientSuccessManager.id)
      : '',
    title: contact.title ?? '',
    contactRole: contact.contactRole ?? '',
    department: contact.department ?? '',
    email: contact.email ?? '',
    secondaryEmail: contact.secondaryEmail ?? '',
    countryCode: contact.countryCode ?? '',
    mobile: contact.mobile ?? '',
    phone: contact.phone ?? '',
    website: contact.website ?? '',
    linkedin: contact.linkedin ?? '',
    // Slice the date part rather than localising it: `new Date()` on a UTC
    // midnight timestamp moves the birthday back a day west of UTC.
    dateOfBirth: toDateInputValue(contact.dateOfBirth),
    /*
     * ⚠️ NEVER `contact.leadSource ?? ''`, which is what stood here. The wire
     * shape changed to `{id, name}` on 2026-08-27 and that expression put
     * `[object Object]` into this text input — which this form then WROTE BACK
     * over the stored value on the next save of any unrelated field. Silent,
     * unrecoverable. `leadSourceInputValue` reads both shapes.
     */
    leadSource: leadSourceInputValue(contact),
    billingStreet: contact.billingStreet ?? '',
    billingCity: contact.billingCity ?? '',
    billingState: contact.billingState ?? '',
    billingCode: contact.billingCode ?? '',
    billingCountry: contact.billingCountry ?? '',
    shippingStreet: contact.shippingStreet ?? '',
    shippingCity: contact.shippingCity ?? '',
    shippingState: contact.shippingState ?? '',
    shippingCode: contact.shippingCode ?? '',
    shippingCountry: contact.shippingCountry ?? '',
    description: contact.description ?? '',
    emailOptOut: contact.emailOptOut,
  };
}

/** True for a saved contact with neither name part stored (a legacy row). */
function partsWereGuessedOf(contact: Contact | undefined): boolean {
  return contact !== undefined && !(contact.firstName?.trim() || contact.lastName?.trim());
}

/**
 * The PATCH baseline: the record, except that a legacy contact's missing parts
 * are seeded with `splitFullName`'s GUESS — the same guess the form shows. So an
 * untouched guess is not a change (Save stays disabled, nothing is sent), and
 * `buildPatch` sends both parts once the person edits either one.
 */
function baselineForm(contact: Contact | undefined): FormState {
  const base = recordForm(contact);
  return partsWereGuessedOf(contact) ? { ...base, ...splitFullName(base.name) } : base;
}

/**
 * Starting values: the baseline, with a locked company overriding the
 * selection, and — on a create only — the prefill.
 *
 * The name parts come from the record or the prefill when either stored them.
 * When neither did (a legacy contact, an enquiry that carried one string),
 * they are `splitFullName`'s GUESS, put on screen so the person can see and
 * correct it. On an edit the guess is also the baseline (`baselineForm`), so it
 * is stored only when the person changes a part.
 */
function startingForm(
  contact: Contact | undefined,
  lockedId: number | null,
  prefill: ContactPrefill | undefined,
): FormState {
  const base = baselineForm(contact);
  const located = lockedId === null ? base : { ...base, companyId: String(lockedId) };
  if (contact) return located;
  if (!prefill) return located;
  const name = prefill.name.trim();
  const parts = prefillHasParts(prefill)
    ? { firstName: prefill.firstName?.trim() ?? '', lastName: prefill.lastName?.trim() ?? '' }
    : splitFullName(name);
  return {
    ...located,
    name: name || composeFullName(parts.firstName, parts.lastName),
    ...parts,
    email: prefill.email?.trim() ?? '',
    phone: prefill.phone?.trim() ?? '',
    title: prefill.title?.trim() ?? '',
  };
}

/** True when the prefill carried its own name parts, so they are not the form's guess. */
function prefillHasParts(prefill: ContactPrefill | undefined): boolean {
  return Boolean(prefill?.firstName?.trim() || prefill?.lastName?.trim());
}

/**
 * The record's own account as a Combobox option, or null.
 *
 * Seeds the "pinned" option — see `companyOptions` — so the account a contact
 * already has survives a search that doesn't return it.
 */
function contactCompanyOption(contact: Contact | undefined): ComboboxOption | null {
  if (!contact?.company) return null;
  return { value: String(contact.company.id), label: contact.company.name };
}

/**
 * POST body. The three name fields are always sent — `firstName`, `lastName`
 * (null when blank) and `name` — so every contact starts with its parts
 * stored. Anything else the user left blank is omitted rather than sent as an
 * empty string, so the row starts with real nulls and the server's own
 * defaults (layout "Standard", emailOptOut false) apply.
 */
function buildCreate(form: FormState): ContactInput {
  const body: ContactInput = {
    name: form.name.trim(),
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim() || null,
  };

  if (form.companyId) body.companyId = Number(form.companyId);
  for (const key of ID_KEYS) if (form[key]) body[key] = Number(form[key]);
  for (const key of TEXT_KEYS) {
    if (key === 'firstName' || key === 'lastName') continue;
    const value = form[key].trim();
    if (value) body[key] = value;
  }
  if (form.layout.trim()) body.layout = form.layout.trim();
  if (form.dateOfBirth) body.dateOfBirth = form.dateOfBirth;
  if (form.emailOptOut) body.emailOptOut = true;

  return body;
}

/**
 * PATCH body: the diff, and nothing else.
 *
 * Each key is compared against the loaded record and included only when it
 * actually moved, with "" mapped to null so a cleared input clears the column.
 * Spreading the whole record instead would rewrite every column the user never
 * opened. `name` and `layout` are never sent as null — the API rejects a null
 * name and layout is a non-null column — so blanking either is treated as no
 * change rather than as a clear.
 *
 * When the baseline parts were GUESSED (`partsWereGuessed`, a legacy contact),
 * neither part is stored yet, so a change to either one sends BOTH — the parts
 * the person looked at are the parts that are stored.
 */
function buildPatch(form: FormState, baseline: FormState, partsWereGuessed: boolean): ContactInput {
  const body: ContactInput = {};

  const name = form.name.trim();
  if (name && name !== baseline.name.trim()) body.name = name;

  const layout = form.layout.trim();
  if (layout && layout !== baseline.layout.trim()) body.layout = layout;

  if (form.companyId !== baseline.companyId) {
    body.companyId = form.companyId ? Number(form.companyId) : null;
  }

  for (const key of ID_KEYS) {
    if (form[key] !== baseline[key]) body[key] = form[key] ? Number(form[key]) : null;
  }

  for (const key of TEXT_KEYS) {
    const value = form[key].trim();
    if (value !== baseline[key].trim()) body[key] = value || null;
  }

  if (partsWereGuessed && namePartsChanged(form, baseline)) {
    body.firstName = form.firstName.trim() || null;
    body.lastName = form.lastName.trim() || null;
  }

  // Already YYYY-MM-DD on both sides, so this is a plain string comparison.
  if (form.dateOfBirth !== baseline.dateOfBirth) {
    body.dateOfBirth = form.dateOfBirth || null;
  }

  // Coerced, not nullish: send a real boolean or nothing at all.
  if (form.emailOptOut !== baseline.emailOptOut) body.emailOptOut = form.emailOptOut;

  return body;
}

/** Whether either name part differs from the baseline. */
function namePartsChanged(form: FormState, baseline: FormState): boolean {
  return (
    form.firstName.trim() !== baseline.firstName.trim() ||
    form.lastName.trim() !== baseline.lastName.trim()
  );
}

/**
 * `baseline` is null on a create, where First name is always required. On an
 * edit it is required only when the record had one or the parts were changed,
 * so a legacy row with a last name and no first name can still be saved.
 */
function firstNameRequiredFor(form: FormState, baseline: FormState | null): boolean {
  return baseline === null || baseline.firstName.trim() !== '' || namePartsChanged(form, baseline);
}

function validate(form: FormState, baseline: FormState | null): Record<string, string> {
  const errors: Record<string, string> = {};

  if (firstNameRequiredFor(form, baseline) && !form.firstName.trim()) {
    errors.firstName = 'Enter the contact’s first name.';
  }
  if (!form.name.trim()) errors.name = 'Enter the contact’s full name.';

  for (const [key, max] of Object.entries(LIMITS) as [StringKey, number][]) {
    if (errors[key]) continue;
    if (form[key].trim().length > max) {
      errors[key] = `${LABELS[key]} must be ${max} characters or fewer.`;
    }
  }

  for (const key of ['email', 'secondaryEmail'] as const) {
    const value = form[key].trim();
    if (value && !EMAIL_PATTERN.test(value)) errors[key] = 'Enter a valid email address.';
  }

  if (form.dateOfBirth && !DATE_PATTERN.test(form.dateOfBirth)) {
    errors.dateOfBirth = 'Use the format YYYY-MM-DD.';
  }

  return errors;
}

export function ContactFormModal({
  open,
  onClose,
  contact,
  lockedCompany,
  prefill,
  onSaved,
}: ContactFormModalProps) {
  const isEdit = contact !== undefined;
  const lockedId = lockedCompany?.id ?? null;
  // Identity of the loaded record. Changes when a different record — or a newer
  // version of the same one — arrives, and not merely when the parent re-renders.
  const contactKey = contact ? `${contact.id}:${contact.updatedAt}` : 'new';

  const formId = useId();
  const optOutId = useId();

  const [form, setForm] = useState<FormState>(() => startingForm(contact, lockedId, prefill));
  const [baseline, setBaseline] = useState<FormState>(() => baselineForm(contact));
  /* Seeded with `baseline` and reset with it: the baseline parts are a guess. */
  const [partsWereGuessed, setPartsWereGuessed] = useState(() => partsWereGuessedOf(contact));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [openSections, setOpenSections] = useState<Set<SectionId>>(() => new Set());

  const [companies, setCompanies] = useState<Company[]>([]);
  /*
   * ⚠️ `companiesTruncated`, `companiesTotal` and `companiesFailed` are GONE —
   * owner's stricter helper-text ruling, 2026-09-05. All three existed only to
   * word two sentences: the truncation footnote "Showing the first 100 of {n}
   * accounts — keep typing to narrow." and the hint "Accounts couldn't be
   * loaded — save without one and link later." With the sentences removed
   * nothing read them.
   *
   * ⚠️ **The SEARCH is still server-side and still the reason the page is
   * truncated** — see the effect below. A reader typing narrows the real query;
   * they are simply no longer told that the first page is a page.
   */
  /** The term the account Combobox last asked the server for. */
  const [companySearch, setCompanySearch] = useState('');
  /** The term the loaded page answers — null until the first fetch settles. */
  const [loadedSearch, setLoadedSearch] = useState<string | null>(null);
  /**
   * The selected account, remembered by name. The fetched page is now a search
   * result, so the selection can easily sit outside it; pinning keeps the
   * trigger honest and the option list complete.
   */
  const [companyPin, setCompanyPin] = useState<ComboboxOption | null>(() =>
    contactCompanyOption(contact),
  );

  /*
   * `withGroups` is on because BOTH staff pickers below are GATED by a user
   * group: naming somebody outside `account_lead` as `accountLeadId` is a 422 on
   * `/contacts` as well as on `/companies`, create and update alike.
   *
   * ⚠️ **`users` — the un-narrowed directory — is deliberately NOT destructured
   * here any more.** It existed for the Owner picker, which was the one staff
   * field the server did not check and so the one that could not be narrowed;
   * the picker went on 2026-08-31 and the reference went with it.
   *
   * ⚠️ *SUPERSEDED 2026-09-05: "The flags stay: they are hook-level and describe
   * both narrowed lists."* `failed` and `hasMore` are gone too — they fed the
   * staff load-failure and truncation sentences, removed under the owner's
   * stricter helper-text ruling, and nothing else consulted them.
   */
  const { loading: usersLoading, byGroup } = useUserOptions(open, true);

  /*
   * Reopening must not show the previous record's values or the last attempt's
   * errors. Done as a render-phase reset rather than an effect: an effect would
   * paint the stale form for a frame first. Keyed on `contactKey` rather than
   * the `contact` object, so a parent re-render can't wipe the user's typing.
   */
  /* The prefill is part of the identity: two enquiries opened in turn are two
     different starting forms, even with the same company locked. */
  const prefillKey = prefill && !contact ? JSON.stringify(prefill) : '';
  const resetKey = `${open ? 'open' : 'closed'}|${contactKey}|${lockedId ?? ''}|${prefillKey}`;
  const [appliedKey, setAppliedKey] = useState(resetKey);
  if (appliedKey !== resetKey) {
    setAppliedKey(resetKey);
    if (open) {
      setForm(startingForm(contact, lockedId, prefill));
      setBaseline(baselineForm(contact));
      setPartsWereGuessed(partsWereGuessedOf(contact));
      setErrors({});
      setFormError(null);
      setSaving(false);
      /* A prefilled phone lives in the collapsed Details section; open it so a
         value the person is about to save is never hidden from them. */
      setOpenSections(
        !contact && prefill?.phone?.trim() ? new Set<SectionId>(['details']) : new Set(),
      );
      setLoadedSearch(null);
      setCompanySearch('');
      setCompanyPin(contactCompanyOption(contact));
    }
  }

  /*
   * Accounts are searched server-side: the directory is far larger than one
   * page, so filtering the fetched page locally would only ever search the
   * first 100 names. Re-runs whenever the Combobox reports a new term — which
   * it already debounces at 250ms, so there is no second debounce here.
   */
  useEffect(() => {
    if (!open || lockedId !== null) return;
    const controller = new AbortController();
    api
      .listCompanies(
        { search: companySearch, perPage: 100, sort: 'name', order: 'asc' },
        controller.signal,
      )
      .then((result) => {
        if (controller.signal.aborted) return;
        setCompanies(result.data);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setCompanies([]);
      })
      .finally(() => {
        // Marks *which* term the list now answers, so "loading" can be derived
        // during render rather than toggled from inside this effect.
        if (!controller.signal.aborted) setLoadedSearch(companySearch);
      });
    return () => controller.abort();
  }, [open, lockedId, companySearch]);

  const companiesLoading = open && lockedId === null && loadedSearch !== companySearch;

  // Stable identity: the Combobox debounce effect depends on this callback, and
  // a new function each render would restart the timer and never fire.
  const searchCompanies = useCallback((term: string) => setCompanySearch(term), []);

  /*
   * The fetched page may not contain the account this contact already has — and
   * once a search term narrows it, usually will not. Keeping the pinned value
   * in the list stops a filtered page from silently reassigning or clearing the
   * account when the form is saved.
   */
  const companyOptions = useMemo<ComboboxOption[]>(() => {
    const options = companies.map((company) => ({
      value: String(company.id),
      label: company.name,
    }));
    if (companyPin && !options.some((option) => option.value === companyPin.value)) {
      options.unshift(companyPin);
    }
    return options;
  }, [companies, companyPin]);

  /*
   * Staff filter client-side, so this list is the whole directory as fetched.
   * Anyone already assigned to the contact is appended in case they fall
   * outside the fetched page — same guard, same reason.
   */
  /**
   * One picker's options: the people it may offer, plus the person the contact
   * already holds when that list does not carry them.
   *
   * ⚠️ **There used to be ONE list shared by all three pickers, and it cannot be
   * one any more.** Account lead and client success manager offer only their
   * group's members, because the server refuses anybody else. (Owner was the
   * third and offered the whole directory, being the one staff field no server
   * guard checked; it was removed on 2026-08-31, which is why nothing here reads
   * the un-narrowed list.) What has not changed is the injection — the stored
   * holder is always offered, whether they are outside the fetched page or
   * outside the group, because a picker that drops a stored value reassigns the
   * record on the next save. `outsideNote` labels them, and is null while the list is
   * unknown so nobody is accused on the strength of a list that has not arrived.
   */
  function staffOptionsFor(
    offered: readonly UserSummary[],
    held: UserSummary | null | undefined,
    outsideNote: string | null,
  ): ComboboxOption[] {
    const options: ComboboxOption[] = offered.map((user) => ({
      value: String(user.id),
      label: user.name,
      hint: user.email,
    }));
    if (held && !options.some((option) => option.value === String(held.id))) {
      options.push({
        value: String(held.id),
        label: held.name,
        hint: [held.email, outsideNote].filter(Boolean).join(' · '),
      });
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }

  const leadStaff = byGroup[USER_GROUP_SLUGS.accountLead];
  const csmStaff = byGroup[USER_GROUP_SLUGS.clientSuccessManager];

  const staffOptionsByKey: Record<StaffFieldKey, ComboboxOption[]> = {
    accountLeadId: staffOptionsFor(
      leadStaff.users,
      contact?.accountLead,
      outsideGroupNote(leadStaff),
    ),
    clientSuccessManagerId: staffOptionsFor(
      csmStaff.users,
      contact?.clientSuccessManager,
      outsideGroupNote(csmStaff),
    ),
  };

  const staffNarrowingByKey: Record<StaffFieldKey, string | null> = {
    accountLeadId: narrowingNote(leadStaff),
    clientSuccessManagerId: narrowingNote(csmStaff),
  };

  // Derived from the payload itself, so "Save is enabled" and "we have
  // something to send" can never disagree — a PATCH {} is a 422.
  const patch = useMemo(
    () => (isEdit ? buildPatch(form, baseline, partsWereGuessed) : null),
    [isEdit, form, baseline, partsWereGuessed],
  );
  const dirty = patch === null || Object.keys(patch).length > 0;

  const sectionErrors = useMemo(() => {
    const counts: Partial<Record<SectionId, number>> = {};
    for (const key of Object.keys(errors)) {
      const section = FIELD_SECTION[key];
      if (section) counts[section] = (counts[section] ?? 0) + 1;
    }
    return counts;
  }, [errors]);

  const billingFilled = ADDRESS_PAIRS.some(([billing]) => form[billing].trim() !== '');
  const shippingMatches = ADDRESS_PAIRS.every(
    ([billing, shipping]) => form[billing].trim() === form[shipping].trim(),
  );

  function setField(key: StringKey, value: string) {
    if (key !== 'firstName' && key !== 'lastName' && key !== 'name') {
      setForm((previous) => ({ ...previous, [key]: value }));
      clearErrors([key]);
      return;
    }
    /*
     * The three name fields fill each other both ways through `applyNameEdit`
     * (`@/lib/contact-name`) — stateless, so the carry is decided from the
     * values on screen right now. The state write applies the rule to
     * `previous`; the same rule on this render's `form` names the auto-filled
     * fields so their errors clear alongside the typed field's.
     */
    setForm((previous) => ({
      ...previous,
      ...applyNameEdit(
        { firstName: previous.firstName, lastName: previous.lastName, name: previous.name },
        key,
        value,
      ),
    }));
    const current = { firstName: form.firstName, lastName: form.lastName, name: form.name };
    const edited = applyNameEdit(current, key, value);
    clearErrors(
      (['firstName', 'lastName', 'name'] as const).filter(
        (field) => field === key || edited[field] !== current[field],
      ),
    );
  }

  function clearErrors(clears: readonly string[]) {
    setErrors((previous) => {
      if (!clears.some((cleared) => cleared in previous)) return previous;
      const next = { ...previous };
      for (const cleared of clears) delete next[cleared];
      return next;
    });
  }

  function revealSections(fieldErrors: Record<string, string>) {
    setOpenSections((previous) => {
      const next = new Set(previous);
      let changed = false;
      for (const key of Object.keys(fieldErrors)) {
        const section = FIELD_SECTION[key];
        if (section && !next.has(section)) {
          next.add(section);
          changed = true;
        }
      }
      return changed ? next : previous;
    });
  }

  function copyBillingToShipping() {
    // Driven off `ADDRESS_PAIRS` rather than written out, so a sixth pair added
    // to the shared list is copied here without anybody remembering to.
    setForm((previous) => {
      const next = { ...previous };
      for (const [billing, shipping] of ADDRESS_PAIRS) next[shipping] = previous[billing];
      return next;
    });
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    /*
     * ⚠️ **Required from the moment this form could be opened from INSIDE
     * another one** — `ContactPickerField` mounts it within the deal, quote and
     * invoice create modals.
     *
     * The dialog is portalled to `document.body`, so in the DOM it is nobody's
     * child; but React propagates by the REACT tree, where it is a descendant of
     * the form behind it. Without this, saving a contact also submits the
     * half-filled deal underneath — which is exactly the defect `PickerField`'s
     * "add a stage" dialog shipped once, with `tsc` and oxlint both green
     * throughout. `Modal` already stops Escape and Tab for the same reason; this
     * is the third event that has to be contained.
     */
    event.stopPropagation();
    if (saving) return;

    const clientErrors = validate(form, isEdit ? baseline : null);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      setFormError(null);
      revealSections(clientErrors);
      return;
    }

    const body = patch ?? buildCreate(form);
    // Belt and braces: the button is disabled in this state, but an empty patch
    // is a guaranteed 422 and must never leave the client.
    if (isEdit && Object.keys(body).length === 0) return;

    setSaving(true);
    setErrors({});
    setFormError(null);

    try {
      const result = contact
        ? await api.updateContact(contact.id, body)
        : await api.createContact(body);
      onSaved(result.data);
      onClose();
    } catch (error) {
      if (error instanceof ApiError) {
        const mapped: Record<string, string> = {};
        /*
         * Seeded with the messages that belong to no field. A `.strict()`
         * rejection names the offending key with an EMPTY path, so it can only
         * ever be shown at form level — keyed by path it would render nowhere.
         */
        const unmapped: string[] = [...error.formMessages];
        for (const [path, message] of Object.entries(error.fieldErrors)) {
          if (KNOWN_FIELDS.has(path)) mapped[path] = message;
          else unmapped.push(`${path}: ${message}`);
        }
        setErrors(mapped);
        revealSections(mapped);

        if (unmapped.length > 0) setFormError(`${error.message} — ${unmapped.join('; ')}`);
        else if (Object.keys(mapped).length > 0) setFormError(null);
        else if (error.status === 404) setFormError('This contact no longer exists.');
        else setFormError(error.message);
      } else {
        setFormError('Couldn’t reach the server. Check your connection.');
      }
    } finally {
      setSaving(false);
    }
  }

  /* ---- render helpers -------------------------------------------------- */

  function textField(
    key: StringKey,
    options: {
      label?: string;
      type?: string;
      required?: boolean;
      placeholder?: string;
      autoComplete?: string;
      className?: string;
    } = {},
  ) {
    const label = options.label ?? LABELS[key];
    return (
      <div className={options.className}>
        <Field label={label} required={options.required} error={errors[key]}>
          {({ id, 'aria-describedby': describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              name={key}
              type={options.type ?? 'text'}
              value={form[key]}
              maxLength={LIMITS[key]}
              placeholder={options.placeholder}
              autoComplete={options.autoComplete ?? 'off'}
              invalid={Boolean(errors[key])}
              onChange={(event) => setField(key, event.target.value)}
            />
          )}
        </Field>
      </div>
    );
  }

  /*
   * A failed fetch and a truncated one describe different things and can both
   * be true, so they are joined rather than coalesced — a `??` here would drop
   * whichever half lost. The popover footnote carries it as well as the hint,
   * because that is where the empty list is actually being looked at.
   */
  /*
   * ⚠️ `staffFootnote` is GONE — owner's stricter helper-text ruling, 2026-09-05.
   * It joined "Staff could not be loaded." (a load failure) and "Showing the
   * first 100 people by name." (a truncation notice). The hint carried a third,
   * "Staff couldn't be loaded — the list is unread, not empty.", and went with
   * them.
   *
   * ⚠️ The GROUP NOTE is a different species and stays on both surfaces: these
   * pickers are narrowed to a user group `/contacts` enforces, so naming
   * somebody outside it is a live 422 — a refusal, and an explicit keeper.
   */
  function staffField(key: StaffFieldKey) {
    const options = staffOptionsByKey[key];
    const groupNote = staffNarrowingByKey[key];
    return (
      <Field label={LABELS[key]} error={errors[key]} hint={groupNote ?? undefined}>
        {({ id, 'aria-describedby': describedBy }) => (
          // No `onSearch`: the staff directory is one page, so the Combobox
          // filters its own options and typing costs nothing.
          <Combobox
            id={id}
            aria-describedby={describedBy}
            value={form[key]}
            onChange={(value) => setField(key, value)}
            options={options}
            placeholder="Unassigned"
            emptyOptionLabel="Unassigned"
            searchPlaceholder="Search people…"
            loading={usersLoading && options.length === 0}
            footnote={groupNote ?? undefined}
            invalid={Boolean(errors[key])}
          />
        )}
      </Field>
    );
  }

  /*
   * ⚠️ "Locked to the account you came from." is GONE — owner's stricter
   * helper-text ruling, 2026-09-05. It restated a `readOnly` Input rather than
   * naming a refusal.
   *
   * ⚠️ THE LOCK ITSELF IS UNTOUCHED and is the part that matters: a contact
   * created from a document's picker must belong to that document's company,
   * because `contacts.service.ts` checks a contact for EXISTENCE and never
   * against the company — a person raised against the wrong account is accepted
   * with a `201` and nothing flags it. `readOnly` + `aria-readonly` still carry
   * that to every reader, assistive tech included.
   */
  const companyField = lockedCompany ? (
    <Field label={LABELS.companyId}>
      {({ id, 'aria-describedby': describedBy }) => (
        <Input
          id={id}
          aria-describedby={describedBy}
          value={lockedCompany.name}
          readOnly
          aria-readonly="true"
          className="bg-surface-sunken text-fg-secondary"
        />
      )}
    </Field>
  ) : (
    <Field label={LABELS.companyId} error={errors.companyId}>
      {({ id, 'aria-describedby': describedBy }) => (
        // `onSearch` hands the term to the server — the account list is far
        // longer than the 100 rows one page returns, so local filtering would
        // only ever search the first page.
        <Combobox
          id={id}
          aria-describedby={describedBy}
          value={form.companyId}
          onChange={(value) => {
            setCompanyPin(companyOptions.find((option) => option.value === value) ?? null);
            setField('companyId', value);
          }}
          options={companyOptions}
          placeholder="No account"
          emptyOptionLabel="No account"
          searchPlaceholder="Search accounts…"
          onSearch={searchCompanies}
          loading={companiesLoading}
          invalid={Boolean(errors.companyId)}
        />
      )}
    </Field>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      dismissOnBackdrop={false}
      title={isEdit ? 'Edit contact' : 'New contact'}
      footer={
        <>
          {/*
            "No changes yet." stood here and is GONE — owner's stricter
            helper-text ruling, 2026-09-05. Save is still disabled on `!dirty`,
            so the control is unchanged; only the narration went.
          */}
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={formId}
            loading={saving}
            disabled={isEdit && !dirty}
          >
            {isEdit ? 'Save changes' : 'Create contact'}
          </Button>
        </>
      }
    >
      <form id={formId} noValidate onSubmit={handleSubmit} className="grid gap-3">
        {formError && (
          <Notice tone="danger" role="alert">
            {formError}
          </Notice>
        )}

        <section className="rounded-lg border border-border bg-surface">
          <h3 className="border-b border-border px-3 py-2.5 text-sm font-medium text-fg">
            Essentials
          </h3>
          <div className="grid gap-3 px-3 py-3 sm:grid-cols-2">
            {/*
              * First name · Last name · Full name, in that order on every
              * contact form (owner, 2026-10-16). The three fill each other
              * both ways while they agree — see `setField` and
              * `applyNameEdit` in `@/lib/contact-name`.
              */}
            {textField('firstName', {
              required: firstNameRequiredFor(form, isEdit ? baseline : null),
              autoComplete: 'given-name',
            })}
            {textField('lastName', { autoComplete: 'family-name' })}
            {textField('name', {
              required: true,
              autoComplete: 'name',
              className: 'sm:col-span-2',
            })}
            {companyField}
            <div>
              <Field label={LABELS.title} error={errors.title}>
                {({ id, 'aria-describedby': describedBy }) => (
                  <ContactTitleInput
                    id={id}
                    aria-describedby={describedBy}
                    value={form.title}
                    onChange={(value) => setField('title', value)}
                    invalid={Boolean(errors.title)}
                  />
                )}
              </Field>
            </div>
            {textField('email', { type: 'email', autoComplete: 'email' })}
            {textField('mobile', { type: 'tel' })}
            {/*
              ⚠️ THE OWNER PICKER STOOD HERE; THE SLOT IS DELIBERATELY EMPTY.
              Owner is gone from contacts (owner's instruction, 2026-08-31) and
              `ownerId` is not a member of `ID_KEYS` or `StringKey`, so nothing
              here can write it. Account lead — a DIFFERENT person, not a
              rename — is in the Relationship section below.
            */}
          </div>
        </section>

        <Disclosure
          id="role"
          title="Role and ownership"
          open={openSections.has('role')}
          errorCount={sectionErrors.role ?? 0}
          onToggle={(next) => toggleSection(setOpenSections, 'role', next)}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            {textField('contactRole')}
            {textField('department')}
            {staffField('accountLeadId')}
            {staffField('clientSuccessManagerId')}
            {textField('leadSource', { className: 'sm:col-span-2' })}
          </div>
        </Disclosure>

        <Disclosure
          id="details"
          title="Contact details"
          open={openSections.has('details')}
          errorCount={sectionErrors.details ?? 0}
          onToggle={(next) => toggleSection(setOpenSections, 'details', next)}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            {textField('secondaryEmail', { type: 'email' })}
            {textField('countryCode')}
            {textField('phone', { type: 'tel' })}
            {textField('website')}
            {textField('linkedin', { className: 'sm:col-span-2' })}
          </div>
        </Disclosure>

        <Disclosure
          id="personal"
          title="Personal"
          open={openSections.has('personal')}
          errorCount={sectionErrors.personal ?? 0}
          onToggle={(next) => toggleSection(setOpenSections, 'personal', next)}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={LABELS.dateOfBirth} error={errors.dateOfBirth}>
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  name="dateOfBirth"
                  type="date"
                  value={form.dateOfBirth}
                  invalid={Boolean(errors.dateOfBirth)}
                  onChange={(event) => setField('dateOfBirth', event.target.value)}
                />
              )}
            </Field>

            <div className="grid gap-1.5">
              <span className="text-xs font-medium text-fg-secondary">Email preferences</span>
              <div className="flex items-center gap-2.5">
                <input
                  id={optOutId}
                  name="emailOptOut"
                  type="checkbox"
                  checked={form.emailOptOut}
                  aria-describedby={errors.emailOptOut ? `${optOutId}-error` : undefined}
                  onChange={(event) =>
                    setForm((previous) => ({ ...previous, emailOptOut: event.target.checked }))
                  }
                  className="h-4 w-4 shrink-0 cursor-pointer rounded-sm border border-hairline-strong accent-accent-brand"
                />
                <label
                  htmlFor={optOutId}
                  className="flex min-h-11 cursor-pointer flex-col justify-center text-sm text-fg-secondary sm:min-h-9"
                >
                  {LABELS.emailOptOut}
                </label>
              </div>
              {errors.emailOptOut && (
                <p id={`${optOutId}-error`} className="text-xs break-words text-danger">
                  {errors.emailOptOut}
                </p>
              )}
            </div>
          </div>
        </Disclosure>

        <Disclosure
          id="address"
          title="Address"
          open={openSections.has('address')}
          errorCount={sectionErrors.address ?? 0}
          onToggle={(next) => toggleSection(setOpenSections, 'address', next)}
        >
          <div className="grid gap-5">
            <div role="group" aria-labelledby={`${formId}-billing`}>
              <h4
                id={`${formId}-billing`}
                className="mb-2 text-xs font-semibold text-fg-secondary"
              >
                Billing address
              </h4>
              <div className="grid gap-3 sm:grid-cols-2">
                {textField('billingStreet', { className: 'sm:col-span-2' })}
                {textField('billingCity')}
                {textField('billingState')}
                {textField('billingCode')}
                {textField('billingCountry')}
              </div>
            </div>

            <div role="group" aria-labelledby={`${formId}-shipping`}>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h4
                  id={`${formId}-shipping`}
                  className="text-xs font-semibold text-fg-secondary"
                >
                  Shipping address
                </h4>
                <Button
                  size="sm"
                  onClick={copyBillingToShipping}
                  disabled={!billingFilled || shippingMatches}
                >
                  {SAME_AS_BILLING_LABEL}
                </Button>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {textField('shippingStreet', { className: 'sm:col-span-2' })}
                {textField('shippingCity')}
                {textField('shippingState')}
                {textField('shippingCode')}
                {textField('shippingCountry')}
              </div>
            </div>
          </div>
        </Disclosure>

        <Disclosure
          id="more"
          title="More details"
          open={openSections.has('more')}
          errorCount={sectionErrors.more ?? 0}
          onToggle={(next) => toggleSection(setOpenSections, 'more', next)}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            {textField('layout')}
          </div>
        </Disclosure>

        <Disclosure
          id="notes"
          title="Notes"
          open={openSections.has('notes')}
          errorCount={sectionErrors.notes ?? 0}
          onToggle={(next) => toggleSection(setOpenSections, 'notes', next)}
        >
          <Field label={LABELS.description} hideLabel error={errors.description}>
            {({ id, 'aria-describedby': describedBy }) => (
              <Textarea
                id={id}
                aria-describedby={describedBy}
                name="description"
                rows={5}
                value={form.description}
                maxLength={LIMITS.description}
                invalid={Boolean(errors.description)}
                onChange={(event) => setField('description', event.target.value)}
              />
            )}
          </Field>
        </Disclosure>
      </form>
    </Modal>
  );
}

function toggleSection(
  setOpenSections: React.Dispatch<React.SetStateAction<Set<SectionId>>>,
  id: SectionId,
  next: boolean,
) {
  setOpenSections((previous) => {
    if (previous.has(id) === next) return previous;
    const updated = new Set(previous);
    if (next) updated.add(id);
    else updated.delete(id);
    return updated;
  });
}

interface DisclosureProps {
  id: SectionId;
  title: string;
  open: boolean;
  errorCount: number;
  onToggle: (open: boolean) => void;
  children: ReactNode;
}

/**
 * A native `<details>` so keyboard operation and the expanded/collapsed state
 * come from the platform. The open state is controlled so a failed save can
 * reveal the section holding the error.
 */
function Disclosure({ id, title, open, errorCount, onToggle, children }: DisclosureProps) {
  return (
    <details
      open={open}
      onToggle={(event) => onToggle(event.currentTarget.open)}
      className="rounded-lg border border-border bg-surface"
      data-section={id}
    >
      <summary
        className={cx(
          'flex min-h-11 cursor-pointer list-none items-center gap-2 rounded-lg px-3 py-2.5 select-none sm:min-h-0',
          'text-sm font-medium text-fg',
          'transition-colors duration-150 ease-out hover:bg-surface-hover motion-reduce:transition-none',
          '[&::-webkit-details-marker]:hidden',
        )}
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.75}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="h-4 w-4 shrink-0 text-fg-subtle"
        >
          {open ? <path d="m6 9 6 6 6-6" /> : <path d="m9 18 6-6-6-6" />}
        </svg>
        <span className="min-w-0 flex-1 truncate">{title}</span>
        {errorCount > 0 && (
          <span className="text-2xs font-semibold text-danger">
            {errorCount === 1 ? '1 problem' : `${errorCount} problems`}
          </span>
        )}
      </summary>
      <div className="border-t border-border px-3 py-3">{children}</div>
    </details>
  );
}
